import React, { useEffect, useRef, type HTMLAttributes } from 'react';
import cx from 'classnames';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import ButtonOrLink from '../../ButtonOrLink';

import stylesheet from './MenuItem.css';

export type MenuItemProps = {
  /** `children` will not be supported in the long term.
   * Default to using the `label` prop. */
  children?: React.ReactNode;
  /** Use destructive for actions we want users to be acutely aware due to their destructive nature. Typically, there should only be one per menu. */
  /** Use group titles to group like items inside a menu */
  variant?: 'default' | 'destructive' | 'title';
  /** Divider adds a line above the item. Divider lines separate sections of your menu to increase scannability. */
  divider?: boolean;
  /** Actions that are redirected as links */
  href?: string;
  /** Icon in front of the label */
  prefix?: React.ReactNode;
  /** Icon at the end of the label */
  suffix?: React.ReactNode;
  /** Title of each action item */
  label: string;
  /** Whether the item is disabled from interaction*/
  disabled?: boolean;
  /** Keyboard shortcut symbols for the item */
  shortcuts?: string | string[];
  /** Mouse click event */
  onClick?:
    | (() => void)
    | ((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void);
} & Omit<HTMLAttributes<HTMLAnchorElement | HTMLButtonElement>, 'prefix'>;

export type MenuItemActionProps = {
  /** Divider adds a line above the item. Divider lines separate sections of your menu to increase scannability. */
  divider?: boolean;
  /** Actions that are redirected as links */
  href?: string;
  /** Icon in front of the label */
  prefix?: React.ReactNode;
  /** Title of each action item */
  label: string;
  /** Whether the item is disabled from interaction*/
  disabled?: boolean;
  /** Mouse click event */
  onClick?:
    | (() => void)
    | ((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void);
} & Omit<HTMLAttributes<HTMLAnchorElement | HTMLButtonElement>, 'prefix'>;

const MenuItem = ({
  label,
  prefix,
  suffix,
  onClick,
  href,
  variant,
  divider,
  disabled,
  shortcuts,
  ...rest
}: MenuItemProps) => {
  const isReleased = mcdsFlagCheck(
    'xp_mcds_redesign_components_organisms_part_2',
  );

  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Add .focus-within class when keypress on MenuItem is detected
    const handleKeyDown = () => {
      // eslint-disable-next-line no-unused-expressions
      itemRef.current?.classList.add(stylesheet['focus-within']);
    };
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);

  return (
    isReleased && (
      <>
        {divider && <div className={stylesheet.divider}></div>}
        <div
          className={cx(
            stylesheet.item,
            !onClick && !href ? stylesheet.noEvent : null,
            disabled && stylesheet.disabled,
          )}
          ref={itemRef}
        >
          {onClick || href ? (
            <ButtonOrLink
              onClick={onClick}
              disabled={disabled}
              className={cx(
                stylesheet.label,
                variant === 'destructive'
                  ? stylesheet.destructive
                  : variant === 'title'
                  ? stylesheet.title
                  : null,
              )}
              href={href}
              {...rest}
            >
              <div className={stylesheet.labelPrimary}>
                {prefix} {label}
              </div>
              <div className={stylesheet.suffixContainer}>
                {shortcuts && (
                  <span className={stylesheet.shortcuts}>{shortcuts}</span>
                )}
                {suffix}
              </div>
            </ButtonOrLink>
          ) : (
            <div
              className={cx(
                stylesheet.label,
                variant === 'destructive'
                  ? stylesheet.destructive
                  : variant === 'title'
                  ? stylesheet.title
                  : null,
              )}
            >
              <div className={stylesheet.labelPrimary}>
                {prefix} {label ? label : null}
              </div>
              <div className={stylesheet.suffixContainer}>
                {shortcuts && (
                  <span className={stylesheet.shortcuts}>{shortcuts}</span>
                )}
                {suffix}
              </div>
            </div>
          )}
        </div>
      </>
    )
  );
};

const MenuItemAction = ({
  label,
  onClick,
  href,
  prefix,
  disabled,
  ...rest
}: MenuItemActionProps) => {
  const isReleased = mcdsFlagCheck(
    'xp_mcds_redesign_components_organisms_part_2',
  );
  return (
    isReleased && (
      <div
        className={cx(
          stylesheet.item,
          stylesheet.itemAction,
          disabled && stylesheet.disabled,
        )}
      >
        <ButtonOrLink
          onClick={onClick}
          disabled={disabled}
          className={stylesheet.label}
          href={href}
          {...rest}
        >
          <div className={stylesheet.labelAction}>
            {prefix}
            {label}
          </div>
        </ButtonOrLink>
      </div>
    )
  );
};

export default MenuItem;
export { MenuItemAction };
